import {
  captureAthenaPhoto,
  createNativeCalendarEvent,
  deleteNativeCalendarEvent,
  listNativeCalendarEvents,
  pickAthenaDocuments,
  pickAthenaImage,
  shareAthenaAttachment,
  shareAthenaText,
  updateNativeCalendarEvent,
} from './AthenaCapabilities';

export const ATHENA_ACTION_TYPES = Object.freeze({
  CREATE_CALENDAR_EVENT: 'CREATE_CALENDAR_EVENT',
  UPDATE_CALENDAR_EVENT: 'UPDATE_CALENDAR_EVENT',
  DELETE_CALENDAR_EVENT: 'DELETE_CALENDAR_EVENT',
  CREATE_TASK: 'CREATE_TASK',
  IMPORT_FILE: 'IMPORT_FILE',
  ATTACH_FILE_TO_PROJECT: 'ATTACH_FILE_TO_PROJECT',
  SHARE_CONTENT: 'SHARE_CONTENT',
  TAKE_PHOTO: 'TAKE_PHOTO',
  // A capture can only reach the Worker after an explicit UI submit. The
  // router intentionally does not perform that request on its own.
  CREATE_QUICK_CAPTURE: 'CREATE_QUICK_CAPTURE',
});

const CONFIRMATION_REQUIRED = new Set([
  ATHENA_ACTION_TYPES.CREATE_CALENDAR_EVENT,
  ATHENA_ACTION_TYPES.UPDATE_CALENDAR_EVENT,
  ATHENA_ACTION_TYPES.DELETE_CALENDAR_EVENT,
  ATHENA_ACTION_TYPES.CREATE_TASK,
  ATHENA_ACTION_TYPES.ATTACH_FILE_TO_PROJECT,
  ATHENA_ACTION_TYPES.SHARE_CONTENT,
  ATHENA_ACTION_TYPES.CREATE_QUICK_CAPTURE,
]);

const ACTIONS = new Set(Object.values(ATHENA_ACTION_TYPES));

function cleanText(value, maximum = 180) {
  return typeof value === 'string'
    ? value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, maximum)
    : '';
}

function actionId() {
  try { return `act-${globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)}`; }
  catch { return `act-${Math.random().toString(36).slice(2)}`; }
}

function normalizePickedItems(result, emptyReason) {
  const items = Array.isArray(result?.items) ? result.items.filter(Boolean) : [];
  const ok = result?.cancelled !== true && items.length > 0;
  return {
    ...(result || {}),
    items,
    ok,
    ...(ok ? {} : { reason: result?.cancelled === true ? 'cancelled' : emptyReason }),
  };
}

/**
 * A successful ContentResolver.insert only proves that Android accepted the
 * write call.  Before a UI says an event was synchronized, read the bounded
 * Calendar Provider window back and match the returned provider id.  This is
 * deliberately kept here as well as in the conversational orchestrator so
 * the manual Workspace calendar has the same proof standard.
 */
async function createAndVerifyNativeCalendarEvent(payload, confirmationToken) {
  const created = await createNativeCalendarEvent({ ...payload });
  if (created?.created !== true || !created?.deviceCalendarId || !created?.deviceEventId) {
    return normalizeNativeResult(created);
  }

  const startMs = Number(payload?.startMs);
  if (!Number.isFinite(startMs) || startMs <= 0) {
    return { ok: false, ...created, status: 'calendar_create_not_verifiable' };
  }

  // Keep the lookup narrow so events scheduled far in the future still fit
  // the native provider's maximum query range.
  const registry = await listNativeCalendarEvents({
    deviceCalendarId: String(created.deviceCalendarId),
    startMs: startMs - 60_000,
    endMs: startMs + 60_000,
  }).catch(() => null);
  const verified = registry?.events?.some((event) => (
    String(event?.deviceEventId) === String(created.deviceEventId)
      && String(event?.deviceCalendarId) === String(created.deviceCalendarId)
      && Math.abs(Number(event?.startMs) - startMs) < 2_000
  ));
  if (verified) return normalizeNativeResult(created);

  // A failed read-back must not leave an unverified native copy behind or
  // let the caller describe it as synchronized.  This is a compensating
  // rollback for the explicit action that just created it.
  await deleteNativeCalendarEvent({
    deviceCalendarId: String(created.deviceCalendarId),
    deviceEventId: String(created.deviceEventId),
    confirmed: true,
    confirmationToken,
  }).catch(() => undefined);
  return { ok: false, ...created, status: 'calendar_create_not_verified' };
}

/** Creates a reviewable action; it never performs a side effect by itself. */
export function prepareAthenaAction({ type, payload = {}, summary = '' } = {}) {
  if (!ACTIONS.has(type)) return null;
  return Object.freeze({
    id: actionId(),
    type,
    payload: Object.freeze({ ...payload }),
    summary: cleanText(summary, 320),
    requiresConfirmation: CONFIRMATION_REQUIRED.has(type),
    status: 'proposed',
    createdAt: Date.now(),
  });
}

/**
 * Executes only a user-confirmed, structured action. Text from a model is
 * never treated as an executable instruction by this router.
 */
export async function executeAthenaAction(action, { confirmationToken = '' } = {}) {
  if (!action || !ACTIONS.has(action.type)) return { ok: false, reason: 'invalid_action' };
  const confirmed = !action.requiresConfirmation || cleanText(confirmationToken, 160).length > 0;
  if (!confirmed) return { ok: false, reason: 'confirmation_required' };

  const payload = action.payload || {};
  switch (action.type) {
    case ATHENA_ACTION_TYPES.CREATE_CALENDAR_EVENT:
      return createAndVerifyNativeCalendarEvent(payload, confirmationToken);
    case ATHENA_ACTION_TYPES.UPDATE_CALENDAR_EVENT:
      return normalizeNativeResult(await updateNativeCalendarEvent({ ...payload }));
    case ATHENA_ACTION_TYPES.DELETE_CALENDAR_EVENT:
      return normalizeNativeResult(await deleteNativeCalendarEvent({ ...payload, confirmed: true, confirmationToken }));
    case ATHENA_ACTION_TYPES.IMPORT_FILE:
      return normalizePickedItems(await pickAthenaDocuments({ multiple: payload.multiple !== false }), 'no_files_selected');
    case ATHENA_ACTION_TYPES.TAKE_PHOTO:
      return normalizePickedItems(await captureAthenaPhoto(), 'camera_capture_failed');
    case ATHENA_ACTION_TYPES.SHARE_CONTENT:
      if (payload.item) return normalizeNativeResult(await shareAthenaAttachment(payload.item, payload.title));
      return normalizeNativeResult(await shareAthenaText({ text: payload.text, title: payload.title }));
    // Task and project linking are Worker-backed records. Keeping them
    // proposed here prevents a native handler from creating a second source
    // of truth or bypassing server ownership checks.
    case ATHENA_ACTION_TYPES.CREATE_TASK:
    case ATHENA_ACTION_TYPES.ATTACH_FILE_TO_PROJECT:
    case ATHENA_ACTION_TYPES.CREATE_QUICK_CAPTURE:
      return { ok: false, reason: 'worker_action_required', action };
    default:
      return { ok: false, reason: 'unsupported_action' };
  }
}

export async function pickAthenaGalleryAction() {
  return pickAthenaImage();
}

function normalizeNativeResult(result) {
  if (!result || result.status?.endsWith?.('_failed')) return { ok: false, ...result };
  if (result.status && result.status !== 'ok' && result.status !== 'created' && result.status !== 'updated' && result.status !== 'deleted') {
    return { ok: false, ...result };
  }
  return { ok: true, ...result };
}
